// Cfo13WeekCashTable — the treasurer's 13-week cash grid: weeks as columns,
// category rows (Collections … Taxes), bold Ending cash, current-week marker,
// min-cash "danger line" with red breach cells, and plain-English trough
// callouts above the grid. Pure presentation over `buildCashTableModel`; the
// page owns data fetching, the drill panel, and floor persistence.

import { useEffect, useMemo, useState } from "react";
import { formatCurrencyCompact, type CfoCurrencyDisplay } from "./cfoFormat";
import { Fragment } from "react";
import { isCashTableCategoryKey } from "./cfoCashTableModel";
import type {
  CashTableModel,
  CashTableCategoryKey,
  CashSection,
  CashTroughCallout,
} from "./cfoCashTableModel";

/** Owner-facing names for the sources-and-uses blocks. */
const SECTION_LABEL: Record<CashSection, string> = {
  operating: "Operating",
  investing: "Investing",
  financing: "Financing",
  position: "",
};

export type Cfo13WeekCashTableProps = {
  model: CashTableModel;
  troughs: CashTroughCallout[];
  minCashFloor: number | null;
  /** Use `scoped` when the surrounding figure identifies the sole currency. */
  currencyDisplay?: CfoCurrencyDisplay;
  canEditFloor?: boolean;
  exactAmounts?: boolean;
  onFloorChange?: (floor: number | null, exactFloor?: string) => void;
  onCellSelect?: (
    // Fires for the nine clickable category cells and for the named children
    // under them; totals, balances and section subtotals are not selectable.
    rowKey: CashTableCategoryKey,
    weekStart: string,
    rowIds: string[],
    // The sub-schedule entry, when the cell clicked was a child. A child shares
    // its parent's `rowKey` — the two are told apart by THIS, and by nothing
    // else, so it has to travel with the selection.
    childKey?: string,
  ) => void;
  selectedCell?: {
    rowKey: CashTableCategoryKey;
    weekStart: string;
    childKey?: string;
  } | null;
};

const cellText = (
  amount: number | null,
  currencyDisplay: CfoCurrencyDisplay,
): string => {
  if (amount == null || Math.abs(amount) < 0.005) return "—";
  return formatCurrencyCompact(amount, { display: currencyDisplay });
};

export function Cfo13WeekCashTable({
  model,
  troughs,
  minCashFloor,
  currencyDisplay = "qualified",
  canEditFloor = true,
  onFloorChange,
  exactAmounts = false,
  onCellSelect,
  selectedCell = null,
}: Cfo13WeekCashTableProps) {
  // Sub-schedule children are COLLAPSED by default. Three categories with up to
  // five children each would add fifteen rows to a grid whose whole value is
  // fitting on one screen, and an owner who has not asked to see vendors should
  // not have to scroll past them.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const hasChildren = useMemo(() => {
    const keys = new Set<string>();
    for (const line of model.lines) {
      if (line.kind === "child") keys.add(line.key);
    }
    return keys;
  }, [model.lines]);
  const visibleLines = useMemo(
    () => model.lines.filter((line) => line.kind !== "child" || expanded[line.key]),
    [model.lines, expanded],
  );

  const [editingFloor, setEditingFloor] = useState(false);
  const [floorDraft, setFloorDraft] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [mobileWeekStart, setMobileWeekStart] = useState("");
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    )
      return;
    const media = window.matchMedia("(max-width: 700px)");
    const update = () => setIsMobile(media.matches);
    update();
    media.addEventListener?.("change", update);
    return () => media.removeEventListener?.("change", update);
  }, []);
  useEffect(() => {
    if (
      mobileWeekStart &&
      model.columns.some((column) => column.weekStart === mobileWeekStart)
    )
      return;
    const preferred =
      model.columns.find((column) => column.isCurrent) ||
      model.columns.find((column) => column.status !== "closed") ||
      model.columns[0];
    setMobileWeekStart(preferred?.weekStart || "");
  }, [mobileWeekStart, model.columns]);
  const commitFloor = () => {
    const trimmed = floorDraft.trim();
    setEditingFloor(false);
    if (!onFloorChange) return;
    if (!trimmed) {
      onFloorChange(null);
      return;
    }
    const parsed = Number(trimmed.replace(/[$,\s]/g, ""));
    if (Number.isFinite(parsed)) {
      if (exactAmounts) onFloorChange(parsed, trimmed.replace(/[$,\s]/g, ""));
      else onFloorChange(parsed);
    }
  };
  const actualColumnCount = model.columns.filter(
    (col) => col.status === "closed",
  ).length;
  const forecastColumnCount = model.columns.length - actualColumnCount;
  const isForecastStart = (index: number) =>
    index > 0 &&
    model.columns[index]?.status !== "closed" &&
    model.columns[index - 1]?.status === "closed";
  const mobileColumnIndex = Math.max(
    0,
    model.columns.findIndex((column) => column.weekStart === mobileWeekStart),
  );
  const mobileColumn = model.columns[mobileColumnIndex];
  const mobileLines = useMemo(
    () =>
      // Children are omitted on mobile entirely, not collapsed. A phone-width
      // list of one week's categories has no room for a disclosure column, and
      // an expanded sub-schedule there is a wall of rows between the owner and
      // the ending-cash line they opened the page for.
      model.lines
        .filter((line) => line.kind !== "child")
        .map((line) => ({
          line,
          cell: line.cells[mobileColumnIndex],
        })),
    [mobileColumnIndex, model.lines],
  );
  const mobileTrough = troughs.find(
    (trough) => trough.weekStart === mobileColumn?.weekStart,
  );

  return (
    <div
      className="cfo-cash-table"
      data-testid="cfo-13-week-cash-table"
      data-fg-tour="cfo-13-week-cash-table"
    >
      {troughs.length ? (
        <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
          {troughs.map((trough) => (
            <div
              key={trough.weekStart}
              className="cfo-shell-notice error"
              role="status"
              data-testid="cash-trough-callout"
              data-fg-tour="cash-trough-callout"
            >
              {trough.message}
            </div>
          ))}
        </div>
      ) : null}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 8,
          flexWrap: "wrap",
        }}
      >
        {editingFloor ? (
          <span
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <label
              style={{ fontSize: 13, color: "var(--muted)" }}
              htmlFor="cfo-cash-floor-input"
            >
              Minimum cash floor
            </label>
            <input
              id="cfo-cash-floor-input"
              aria-label="Minimum cash floor"
              value={floorDraft}
              autoFocus
              placeholder="e.g. 50,000 — blank removes it"
              onChange={(event) => setFloorDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") commitFloor();
                if (event.key === "Escape") setEditingFloor(false);
              }}
              onBlur={commitFloor}
              style={{ minHeight: 30, width: 180, padding: "2px 8px" }}
            />
          </span>
        ) : (
          <button
            type="button"
            className="cfo-shell-chip"
            disabled={!canEditFloor}
            title={
              canEditFloor
                ? "Set the minimum-cash danger line"
                : "Read-only in this session"
            }
            onClick={() => {
              setFloorDraft(minCashFloor != null ? String(minCashFloor) : "");
              setEditingFloor(true);
            }}
          >
            {minCashFloor != null
              ? `Min cash floor · ${formatCurrencyCompact(minCashFloor, { display: currencyDisplay })}`
              : "Set a min-cash floor"}
          </button>
        )}
      </div>

      {isMobile && mobileColumn ? (
        <div
          data-testid="cfo-13-week-cash-mobile"
          style={{ display: "grid", gap: 12 }}
        >
          <label style={{ display: "grid", gap: 5, fontSize: 12 }}>
            <span style={{ color: "var(--muted)" }}>Week</span>
            <select
              aria-label="Cash plan week"
              value={mobileColumn.weekStart}
              onChange={(event) => setMobileWeekStart(event.target.value)}
              style={{ minHeight: 42, width: "100%", padding: "6px 10px" }}
            >
              {model.columns.map((column) => (
                <option key={column.weekStart} value={column.weekStart}>
                  {column.label}
                  {column.isCurrent ? " · this week" : ""}
                  {column.status === "closed" ? " · closed" : " · forecast"}
                </option>
              ))}
            </select>
          </label>
          <section
            aria-label={`Cash plan for ${mobileColumn.label}`}
            style={{
              border: "1px solid var(--line, #e5e0d8)",
              borderRadius: 12,
              overflow: "hidden",
            }}
          >
            <header
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 8,
                padding: "10px 12px",
                background:
                  "color-mix(in srgb, var(--acc, #176b87) 5%, transparent)",
              }}
            >
              <strong>{mobileColumn.label}</strong>
              <span style={{ color: "var(--muted)", fontSize: 12 }}>
                {mobileColumn.status === "closed"
                  ? "Posted actual"
                  : "Forecast"}
              </span>
            </header>
            {mobileTrough ? (
              <div className="cfo-shell-notice error" style={{ margin: 10 }}>
                Below your minimum-cash floor
              </div>
            ) : null}
            <div style={{ display: "grid" }}>
              {mobileLines.map(({ line, cell }) => {
                if (!cell) return null;
                const categoryKey = isCashTableCategoryKey(line.key)
                  ? line.key
                  : null;
                const clickable =
                  Boolean(onCellSelect) &&
                  categoryKey != null &&
                  line.kind !== "total" &&
                  line.kind !== "balance" &&
                  cell.rowIds.length > 0;
                const selected =
                  selectedCell?.rowKey === line.key &&
                  (selectedCell?.childKey ?? undefined) ===
                    (line.childKey ?? undefined) &&
                  selectedCell?.weekStart === mobileColumn.weekStart;
                return (
                  <button
                    type="button"
                    key={line.childKey ?? line.key}
                    disabled={!clickable}
                    onClick={() =>
                      clickable &&
                      categoryKey != null &&
                      onCellSelect?.(
                        categoryKey,
                        mobileColumn.weekStart,
                        cell.rowIds,
                        line.childKey,
                      )
                    }
                    style={{
                      alignItems: "center",
                      background: cell.breachesFloor
                        ? "color-mix(in srgb, var(--neg, #b91c1c) 10%, transparent)"
                        : selected
                          ? "color-mix(in srgb, var(--acc, #176b87) 12%, transparent)"
                          : "transparent",
                      border: 0,
                      borderTop: "1px solid var(--line, #e5e0d8)",
                      color: "inherit",
                      cursor: clickable ? "pointer" : "default",
                      display: "grid",
                      font: "inherit",
                      gridTemplateColumns: "minmax(0, 1fr) auto",
                      minHeight: 44,
                      padding: "8px 12px",
                      textAlign: "left",
                      width: "100%",
                    }}
                  >
                    <span>
                      {line.label}
                      {clickable ? (
                        <small
                          style={{
                            color: "var(--muted)",
                            display: "block",
                            marginTop: 2,
                          }}
                        >
                          View evidence
                        </small>
                      ) : null}
                    </span>
                    <strong
                      style={{
                        color: cell.breachesFloor
                          ? "var(--neg, #b91c1c)"
                          : undefined,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {cellText(cell.amount, currencyDisplay)}
                    </strong>
                  </button>
                );
              })}
            </div>
          </section>
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              borderCollapse: "collapse",
              width: "100%",
              minWidth: model.columns.length * 96 + 170,
            }}
            aria-label="Thirteen week cash plan, weeks across the top and cash categories down the side"
          >
            <thead>
              <tr>
                <th
                  style={{
                    position: "sticky",
                    left: 0,
                    zIndex: 2,
                    background: "var(--panel, var(--bg, #fff))",
                    borderBottom: "1px solid var(--line, #e5e0d8)",
                  }}
                />
                {actualColumnCount ? (
                  <th
                    colSpan={actualColumnCount}
                    style={{
                      padding: "5px 8px",
                      textAlign: "center",
                      fontSize: 11,
                      letterSpacing: ".06em",
                      color: "var(--faint, #9a917f)",
                      borderBottom: "1px solid var(--line, #e5e0d8)",
                    }}
                  >
                    Actual · posted cash history
                  </th>
                ) : null}
                {forecastColumnCount ? (
                  <th
                    colSpan={forecastColumnCount}
                    style={{
                      padding: "5px 8px",
                      textAlign: "center",
                      fontSize: 11,
                      letterSpacing: ".06em",
                      color: "var(--acc, #176b87)",
                      borderLeft: actualColumnCount
                        ? "2px solid var(--acc, #176b87)"
                        : undefined,
                      borderBottom: "1px solid var(--line, #e5e0d8)",
                    }}
                  >
                    Forecast · next 13 weeks
                  </th>
                ) : null}
              </tr>
              <tr>
                <th
                  style={{
                    position: "sticky",
                    left: 0,
                    zIndex: 2,
                    background: "var(--panel, var(--bg, #fff))",
                    textAlign: "left",
                    padding: "6px 10px",
                    fontSize: 12,
                    color: "var(--muted)",
                    borderBottom: "1px solid var(--line, #e5e0d8)",
                  }}
                >
                  Category
                </th>
                {model.columns.map((col, index) => (
                  <th
                    key={col.weekStart}
                    style={{
                      padding: "6px 8px",
                      fontSize: 12,
                      whiteSpace: "nowrap",
                      textAlign: "right",
                      color:
                        col.status === "closed"
                          ? "var(--faint, #9a917f)"
                          : "var(--muted)",
                      borderBottom: col.isCurrent
                        ? "2px solid var(--acc, #176b87)"
                        : "1px solid var(--line, #e5e0d8)",
                      fontWeight: col.isCurrent ? 700 : 500,
                      borderLeft: isForecastStart(index)
                        ? "2px solid var(--acc, #176b87)"
                        : undefined,
                    }}
                  >
                    {col.label}
                    {col.isCurrent ? (
                      <div style={{ fontSize: 10, fontWeight: 600 }}>
                        this week
                      </div>
                    ) : null}
                    {col.status === "closed" ? (
                      <div style={{ fontSize: 10 }}>closed</div>
                    ) : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleLines.map((line, lineIndex) => {
                const isTotal = line.kind === "total";
                const isBalance = line.kind === "balance";
                const isSubtotal = line.kind === "subtotal";
                const isChild = line.kind === "child";
                // A child shares its parent's `key`, so every identity that has
                // to be unique per RENDERED ROW — the React key, the test id,
                // the cell keys and the selection compare — must use this.
                const lineId = line.childKey ?? line.key;
                // A section header is drawn when the activity changes, and only
                // for the three activity blocks — never for the beginning/net/
                // ending frame, which belongs to no activity.
                const prevSection = visibleLines[lineIndex - 1]?.section;
                const showSectionHead =
                  line.section != null &&
                  line.section !== "position" &&
                  line.section !== prevSection;
                return (
                  <Fragment key={lineId}>
                  {showSectionHead ? (
                    <tr data-testid={`cash-section-${line.section}`}>
                      {/* The label cell must stay sticky, so the heading cannot
                          be a colSpan across the whole row — a colSpan cell
                          cannot also be position:sticky;left:0. */}
                      <td
                        style={{
                          position: "sticky",
                          left: 0,
                          zIndex: 1,
                          background: "var(--surface, #fffdf8)",
                          padding: "12px 10px 4px",
                          fontSize: 11,
                          fontWeight: 600,
                          letterSpacing: ".08em",
                          textTransform: "uppercase",
                          color: "var(--muted, #868c9a)",
                          whiteSpace: "nowrap",
                          borderTop: "1px solid var(--line, #e5e0d8)",
                        }}
                      >
                        {SECTION_LABEL[line.section as CashSection]}
                      </td>
                      {model.columns.map((col, index) => (
                        <td
                          key={`${line.section}:head:${col?.weekStart || index}`}
                          style={{
                            borderTop: "1px solid var(--line, #e5e0d8)",
                            padding: "12px 8px 4px",
                          }}
                        />
                      ))}
                    </tr>
                  ) : null}
                  <tr data-testid={`cash-row-${lineId}`}>
                    <td
                      style={{
                        position: "sticky",
                        left: 0,
                        zIndex: 1,
                        background: "var(--panel, var(--bg, #fff))",
                        padding: isChild ? "3px 10px 3px 26px" : "5px 10px",
                        fontSize: isChild ? 12 : 13,
                        whiteSpace: "nowrap",
                        fontWeight:
                          isTotal || isSubtotal ? 700 : isBalance ? 600 : 500,
                        color: isChild ? "var(--muted, #868c9a)" : undefined,
                        // Modeled children carry no document behind them. The
                        // italic is the same signal the drill uses, so an owner
                        // can see at a glance how much of a category is
                        // inferred rather than scheduled.
                        fontStyle:
                          isChild && line.provenance === "modeled"
                            ? "italic"
                            : undefined,
                        borderTop:
                          line.key === "net" || isSubtotal
                            ? "1px solid var(--line, #e5e0d8)"
                            : undefined,
                      }}
                    >
                      {hasChildren.has(line.key) && line.kind !== "child" ? (
                        <button
                          type="button"
                          data-testid={`cash-row-toggle-${line.key}`}
                          aria-expanded={Boolean(expanded[line.key])}
                          onClick={() =>
                            setExpanded((current) => ({
                              ...current,
                              [line.key]: !current[line.key],
                            }))
                          }
                          style={{
                            font: "inherit",
                            fontWeight: "inherit",
                            background: "none",
                            border: 0,
                            padding: 0,
                            cursor: "pointer",
                            color: "inherit",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <span
                            aria-hidden="true"
                            style={{
                              fontSize: 9,
                              color: "var(--muted, #868c9a)",
                              transform: expanded[line.key] ? "rotate(90deg)" : "none",
                              display: "inline-block",
                            }}
                          >
                            ▶
                          </span>
                          {line.label}
                        </button>
                      ) : (
                        line.label
                      )}
                    </td>
                    {line.cells.map((cell, index) => {
                      const col = model.columns[index];
                      const categoryKey = isCashTableCategoryKey(line.key)
                        ? line.key
                        : null;
                      const clickable =
                        Boolean(onCellSelect) &&
                        categoryKey != null &&
                        !isTotal &&
                        !isBalance &&
                        cell.rowIds.length > 0;
                      const selected =
                        selectedCell != null &&
                        selectedCell.rowKey === line.key &&
                        (selectedCell.childKey ?? undefined) ===
                          (line.childKey ?? undefined) &&
                        selectedCell.weekStart === col?.weekStart;
                      return (
                        <td
                          key={`${lineId}:${col?.weekStart || index}`}
                          onClick={
                            clickable && col
                              ? () =>
                                  categoryKey != null &&
                                  onCellSelect?.(
                                    categoryKey,
                                    col.weekStart,
                                    cell.rowIds,
                                    line.childKey,
                                  )
                              : undefined
                          }
                          role={clickable ? "button" : undefined}
                          style={{
                            padding: "5px 8px",
                            fontSize: 13,
                            textAlign: "right",
                            whiteSpace: "nowrap",
                            fontVariantNumeric: "tabular-nums",
                            cursor: clickable ? "pointer" : undefined,
                            fontWeight:
                              line.key === "ending"
                                ? 700
                                : isTotal || isSubtotal
                                  ? 650
                                  : 400,
                            color: cell.breachesFloor
                              ? "var(--neg, #b91c1c)"
                              : col?.status === "closed"
                                ? "var(--faint, #9a917f)"
                                : cell.amount != null &&
                                    cell.amount < 0 &&
                                    line.kind !== "balance"
                                  ? "var(--ink-2, #4a4438)"
                                  : undefined,
                            background: cell.breachesFloor
                              ? "color-mix(in srgb, var(--neg, #b91c1c) 10%, transparent)"
                              : cell.couldBreachFloor
                                ? "color-mix(in srgb, var(--neg, #b91c1c) 4%, transparent)"
                                : selected
                                  ? "color-mix(in srgb, var(--acc, #176b87) 12%, transparent)"
                                  : col?.isCurrent
                                    ? "color-mix(in srgb, var(--acc, #176b87) 5%, transparent)"
                                    : undefined,
                            borderTop:
                              line.key === "net" || isSubtotal
                                ? "1px solid var(--line, #e5e0d8)"
                                : undefined,
                            borderLeft: isForecastStart(index)
                              ? "2px solid var(--acc, #176b87)"
                              : undefined,
                            textDecoration: clickable
                              ? "underline dotted 1px"
                              : undefined,
                            textUnderlineOffset: clickable ? 3 : undefined,
                          }}
                        >
                          {cellText(cell.amount, currencyDisplay)}
                          {cell.low != null && cell.high != null ? (
                            <div
                              style={{
                                fontSize: 10.5,
                                fontWeight: 400,
                                color: cell.couldBreachFloor
                                  ? "var(--neg, #b91c1c)"
                                  : "var(--muted)",
                                whiteSpace: "nowrap",
                              }}
                              title={
                                cell.couldBreachFloor
                                  ? "The expected path clears your floor, but this week could dip below it"
                                  : "If this week alone lands at the low or high end of the model's range"
                              }
                            >
                              {cellText(cell.low, currencyDisplay)} –{" "}
                              {cellText(cell.high, currencyDisplay)}
                            </div>
                          ) : null}
                        </td>
                      );
                    })}
                  </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Cfo13WeekCashTable;
